import React, { useContext, useEffect, useRef, useState } from "react";
import {
  BlockStack,
  Box,
  Layout,
  LegacyCard as Card,
  FormLayout,
  RadioButton,
  Text,
} from "@shopify/polaris";

// import context
import { PrivateContext } from "lib/context";

// import components
import { Banner /* Sheet */ } from "lib/components";

// import cms and constants
import constant from "lib/constant/constant";

// import hoc
import { withErrorBoundary } from "lib/hoc";

import CreateForm from "./subFeatures/form/form";
import InviteVendor from "./subFeatures/invite/invite";
import VendorBulkInvite from "./subFeatures/bulkInvite/bulkInvite";
import BulkCreate from "./subFeatures/bulkCreate/bulkCreate";

const { CREATE_FORM, INVITE_CSV, CREATE_CSV, INVITE_FORM } = constant;

const AdminVendorAdd = () => {
  const { cms = {} } = useContext(PrivateContext);
  const addVendorRadioOptions = [
    {
      label: cms("section.addVendorMethod.label.invite"),
      value: INVITE_FORM,
      helpText: cms("section.addVendorMethod.helpText.invite"),
    },
    {
      label: cms("section.addVendorMethod.label.bulkInvite"),
      value: INVITE_CSV,
      helpText: cms("section.addVendorMethod.helpText.bulkInvite"),
    },
    {
      label: cms("section.addVendorMethod.label.createForm"),
      value: CREATE_FORM,
      helpText: cms("section.addVendorMethod.helpText.createForm"),
    },
    {
      label: cms("section.addVendorMethod.label.bulkCreate"),
      value: CREATE_CSV,
      helpText: cms("section.addVendorMethod.helpText.bulkCreate"),
    },
  ];
  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
    onDismiss: null,
  });
  const [selected, setSelected] = useState(CREATE_FORM);
  const formSectionRef = useRef(null);
  const shouldScrollToFormRef = useRef(false);
  const formScrollFrameRef = useRef(null);

  const renderComponent = (value) => {
    switch (value) {
      case CREATE_FORM:
        return <CreateForm setBanner={setBanner} />;
      case INVITE_CSV:
        return <VendorBulkInvite setBanner={setBanner} />;
      case CREATE_CSV:
        return <BulkCreate setBanner={setBanner} />;
      case INVITE_FORM:
        return <InviteVendor setBanner={setBanner} />;
      default:
        return null;
    }
  };

  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const handleSelect = (updatedValue) => {
    shouldScrollToFormRef.current = true;
    setSelected(updatedValue);
    setBanner({ isOpen: false, status: "", title: "" });
  };

  useEffect(() => {
    if (!shouldScrollToFormRef.current) {
      return undefined;
    }

    shouldScrollToFormRef.current = false;
    window.cancelAnimationFrame(formScrollFrameRef.current);

    formScrollFrameRef.current = window.requestAnimationFrame(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });

    return undefined;
  }, [selected]);

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(formScrollFrameRef.current);
    };
  }, []);

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <Layout.AnnotatedSection
        title={cms("section.addVendorMethod.title")}
        description={cms("section.addVendorMethod.description")}
      >
        <Card title={cms("section.onboarding.addSection.title")}>
          <Card.Section>
            <FormLayout>
              <BlockStack gap="200">
                <Text as="p">{cms("description")}</Text>
              </BlockStack>
            </FormLayout>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {cms("section.addVendorMethod.chooseMethod")}
              </Text>
              {addVendorRadioOptions.map((option) => (
                <Box
                  key={option.value}
                  padding="200"
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="300"
                >
                  <RadioButton
                    id={`addVendorType-${option.value}`}
                    label={option.label}
                    helpText={option.helpText}
                    checked={selected === option.value}
                    onChange={() => handleSelect(option.value)}
                  />
                </Box>
              ))}
            </BlockStack>
          </Card.Section>
        </Card>
      </Layout.AnnotatedSection>
      <Box ref={formSectionRef} className="vendor-add-form-scroll-anchor" />
      {renderComponent(selected)}
    </>
  );
};
export default withErrorBoundary(AdminVendorAdd);
